import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/hooks/useSeo";
import { Navigate } from "react-router-dom";
import { CalendarPlus, CalendarClock, ShieldAlert, X, Trash2, FileEdit, CheckCircle2 } from "lucide-react";
import SessionReportModal from "./SessionReportModal";

const SUBJECTS = ["Math", "English", "Science", "Social Studies", "French",
                    "Physics", "Chemistry", "Biology", "Coding", "Reading"];

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-CA", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function TeacherDashboard() {
  useSeo({ title: "Teacher Dashboard | Teacher4Me Learning Centre" });
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [slots, setSlots] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reportSession, setReportSession] = useState(null);   // modal target

  // form
  const [date, setDate] = useState("");
  const [start, setStart] = useState("16:00");
  const [end, setEnd] = useState("17:00");
  const [subject, setSubject] = useState("Math");
  const [mode, setMode] = useState("physical");

  const load = async () => {
    try {
      const s = await api.get("/api/centre/teacher/status");
      setStatus(s.data.status);
      if (s.data.status === "approved") {
        const [a, b] = await Promise.all([
          api.get("/api/centre/teacher/slots"),
          api.get("/api/centre/teacher/sessions"),
        ]);
        setSlots(a.data.items || []);
        setSessions(b.data.items || []);
      }
    } catch (err) { toast.error("Load failed"); }
  };

  useEffect(() => { if (user && user.role === "teacher") load(); }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "teacher") return <Navigate to="/" replace />;

  const addSlot = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Pick a date");
    const iso = (t) => new Date(`${date}T${t}:00`).toISOString();
    setBusy(true);
    try {
      await api.post("/api/centre/teacher/slots", {
        slots: [{ start_at: iso(start), end_at: iso(end), subject, mode }],
      });
      toast.success("Slot added");
      setDate("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add slot");
    } finally { setBusy(false); }
  };

  const cancelSlot = async (id) => {
    if (!window.confirm("Cancel this open slot?")) return;
    try {
      await api.delete(`/api/centre/teacher/slots/${id}`);
      toast.success("Slot cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  const cancelSession = async (id) => {
    const reason = window.prompt("Reason for cancelling this session? (visible to parent)");
    if (reason == null) return;
    try {
      await api.post(`/api/centre/sessions/${id}/cancel`, { reason });
      toast.success("Session cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  // Memoise session partitions — avoids re-filtering on every render.
  // MUST be declared before any conditional early-return to keep hook order stable.
  const openSlots = slots.filter((s) => s.status === "open");
  const nowMs = Date.now();
  const upcomingSessions = sessions.filter((s) =>
    s.status === "scheduled" && new Date(s.end_at).getTime() > nowMs);
  const awaitingReport = sessions.filter((s) =>
    s.status === "scheduled" && new Date(s.end_at).getTime() <= nowMs);
  const completed = sessions.filter((s) => s.status === "completed");
  const cancelled = sessions.filter((s) => s.status === "cancelled");

  if (status && status !== "approved") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-2xl p-8" data-testid="teacher-not-approved">
          <div className="flex items-center gap-2 text-[#92400E] font-semibold">
            <ShieldAlert className="w-5 h-5" /> Application status: <b>{status}</b>
          </div>
          <p className="text-sm text-[#4B5563] mt-3">
            You can start publishing availability and receiving bookings once our team approves
            your application. You'll get an email when your status changes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="teacher-dashboard">
      <div className="mb-6">
        <div className="label-eyebrow">Teacher</div>
        <h1 className="font-display font-black text-3xl mt-1">Your schedule</h1>
        <p className="text-sm text-[#4B5563] mt-1">
          Publish availability. Parents can book approved subjects and modes.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4">
            <CalendarPlus className="w-5 h-5 text-[#D92D20]" /> Add availability
          </h2>
          <form onSubmit={addSlot} className="grid grid-cols-2 gap-3" data-testid="slot-form">
            <div className="col-span-2">
              <label className="text-sm font-semibold">Date</label>
              <input type="date" required value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="slot-date"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-semibold">Start</label>
              <input type="time" required value={start} onChange={(e) => setStart(e.target.value)}
                data-testid="slot-start"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-semibold">End</label>
              <input type="time" required value={end} onChange={(e) => setEnd(e.target.value)}
                data-testid="slot-end"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-semibold">Subject</label>
              <select value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="slot-subject"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
                {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Mode</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}
                data-testid="slot-mode"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
                <option value="physical">physical</option>
                <option value="online">online</option>
              </select>
            </div>
            <div className="col-span-2">
              <button type="submit" disabled={busy} data-testid="slot-add-btn" className="btn-primary w-full">
                {busy ? "Adding..." : "Add slot"}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">Your open slots ({openSlots.length})</div>
            <ul className="divide-y border border-[#E5E7EB] rounded-md">
              {openSlots.length === 0 && (
                <li className="p-3 text-sm text-[#4B5563]" data-testid="empty-open-slots">No open slots.</li>
              )}
              {openSlots.map((s) => (
                <li key={s.id} className="p-3 flex items-center justify-between"
                  data-testid={`open-slot-${s.id.slice(0, 8)}`}>
                  <div className="text-sm">
                    <div className="font-semibold">{fmt(s.start_at)} → {new Date(s.end_at).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })}</div>
                    <div className="text-xs text-[#4B5563]">{s.subject || "Any"} · {s.mode}</div>
                  </div>
                  <button onClick={() => cancelSlot(s.id)} data-testid={`cancel-slot-${s.id.slice(0, 8)}`}
                    className="text-[#B91C1C] hover:bg-[#FEF2F2] p-1 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4">
            <CalendarClock className="w-5 h-5 text-[#064E3B]" /> Upcoming sessions
          </h2>

          {/* Awaiting report — past sessions with no report yet */}
          {awaitingReport.length > 0 && (
            <div className="mb-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#92400E] mb-2">
                <FileEdit className="w-4 h-4" /> {awaitingReport.length} session(s) awaiting report
              </div>
              <ul className="divide-y divide-[#FDE68A]/60">
                {awaitingReport.map((s) => (
                  <li key={s.id} className="py-2 flex items-center justify-between"
                    data-testid={`awaiting-report-${s.id.slice(0, 8)}`}>
                    <div className="text-sm">
                      <div className="font-semibold">{fmt(s.start_at)}</div>
                      <div className="text-xs text-[#4B5563]">
                        {s.subject || "—"} · with <b>{s.student_name}</b>
                      </div>
                    </div>
                    <button onClick={() => setReportSession(s)}
                      data-testid={`open-report-${s.id.slice(0, 8)}`}
                      className="btn-primary text-xs px-3 py-1.5">Submit Report</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {upcomingSessions.length === 0 && awaitingReport.length === 0 && (
            <div className="text-sm text-[#4B5563]" data-testid="empty-teacher-sessions">
              No scheduled sessions yet. Once a parent books an open slot it will appear here.
            </div>
          )}
          <ul className="divide-y border border-[#E5E7EB] rounded-md">
            {upcomingSessions.map((s) => (
              <li key={s.id} className="p-4" data-testid={`teacher-session-${s.id.slice(0, 8)}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-sm">{fmt(s.start_at)}</div>
                    <div className="text-xs text-[#4B5563]">{s.subject || "—"} · {s.mode}</div>
                    <div className="text-sm mt-1">
                      Student: <b>{s.student_name}</b> {s.student_grade ? `· ${s.student_grade}` : ""}
                    </div>
                  </div>
                  <button onClick={() => cancelSession(s.id)}
                    data-testid={`cancel-session-${s.id.slice(0, 8)}`}
                    className="text-xs text-[#B91C1C] font-semibold flex items-center gap-1 hover:underline">
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {completed.length > 0 && (
            <details className="mt-4 text-sm" open>
              <summary className="cursor-pointer font-semibold text-[#4B5563] flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4 text-[#064E3B]" /> Completed sessions ({completed.length})
              </summary>
              <ul className="mt-2 divide-y border border-[#E5E7EB] rounded-md">
                {completed.map((s) => (
                  <li key={s.id} className="p-3 flex items-center justify-between text-sm"
                    data-testid={`completed-session-${s.id.slice(0, 8)}`}>
                    <div>
                      <div className="font-semibold">{fmt(s.start_at)}</div>
                      <div className="text-xs text-[#4B5563]">
                        {s.subject || "—"} · <b>{s.student_name}</b>
                        {s.report ? ` · rated ${s.report.performance_rating}/5` : ""}
                      </div>
                    </div>
                    <button onClick={() => setReportSession(s)}
                      data-testid={`edit-report-${s.id.slice(0, 8)}`}
                      className="text-xs font-semibold text-[#D92D20] hover:underline">
                      Edit report
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {cancelled.length > 0 && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer font-semibold text-[#4B5563]">
                Cancelled ({cancelled.length})
              </summary>
              <ul className="mt-2 divide-y border border-[#E5E7EB] rounded-md">
                {cancelled.map((s) => (
                  <li key={s.id} className="p-3 text-sm">
                    <div className="font-semibold">{fmt(s.start_at)}</div>
                    <div className="text-xs text-[#4B5563]">
                      {s.subject || "—"} · {s.cancellation_reason || "no reason given"}
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      </div>

      {reportSession && (
        <SessionReportModal
          session={reportSession}
          onClose={() => setReportSession(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
